import React, { useEffect, useState, useRef } from "react";
import { db } from "../firebase";
import { collection, addDoc, query, where, onSnapshot, deleteDoc, doc, updateDoc } from "firebase/firestore";
import MikrofonButton from "../components/MikrofonButton";

function bildKomprimieren(datei) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(datei);
    img.onload = () => {
      const maxB = 900;
      const scale = img.width > maxB ? maxB / img.width : 1;
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL("image/jpeg", 0.65));
    };
    img.onerror = reject;
    img.src = url;
  });
}

const LEER = { titel: "", beschreibung: "", geschaeft: "", preis: "", vonDatum: "", bisDatum: "", bild: null };

export default function Angebote({ user }) {
  const [angebote, setAngebote] = useState([]);
  const [formular, setFormular] = useState(LEER);
  const [bearbeitenId, setBearbeitenId] = useState(null);
  const [offen, setOffen] = useState(false);
  const [bildLaed, setBildLaed] = useState(false);
  const bildRef = useRef();
  const kameraRef = useRef();
  const pasteRef = useRef(null);

  useEffect(() => {
    const q = query(collection(db, "angebote"), where("uid", "==", user.uid));
    return onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.erstellt || 0) - (a.erstellt || 0));
      setAngebote(data);
    });
  }, [user.uid]);

  // Paste-Listener für Screenshot
  pasteRef.current = async (datei) => {
    setBildLaed(true);
    try {
      const base64 = await bildKomprimieren(datei);
      setFormular((f) => ({ ...f, bild: base64 }));
    } catch (e) { alert("Fehler: " + e.message); }
    setBildLaed(false);
  };

  useEffect(() => {
    const handlePaste = (e) => {
      if (!offen) return;
      let datei = null;
      const items = e.clipboardData?.items;
      if (items) {
        for (const item of Array.from(items)) {
          if (item.kind === "file" && item.type.startsWith("image/")) {
            datei = item.getAsFile();
            if (datei) break;
          }
        }
      }
      if (datei) { e.preventDefault(); setTimeout(() => pasteRef.current(datei), 0); }
    };
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [offen]);

  const bildHochladen = async (datei) => {
    if (!datei) return;
    setBildLaed(true);
    try {
      const base64 = await bildKomprimieren(datei);
      setFormular((f) => ({ ...f, bild: base64 }));
    } catch (e) { alert("Fehler: " + e.message); }
    setBildLaed(false);
  };

  const speichern = async (e) => {
    e.preventDefault();
    if (!formular.titel.trim()) return;
    const daten = { ...formular, uid: user.uid, erstellt: Date.now() };
    if (bearbeitenId) {
      await updateDoc(doc(db, "angebote", bearbeitenId), daten);
    } else {
      await addDoc(collection(db, "angebote"), daten);
    }
    setFormular(LEER);
    setBearbeitenId(null);
    setOffen(false);
  };

  const bearbeiten = (a) => {
    setFormular({ titel: a.titel || "", beschreibung: a.beschreibung || "", geschaeft: a.geschaeft || "", preis: a.preis || "", vonDatum: a.vonDatum || "", bisDatum: a.bisDatum || "", bild: a.bild || null });
    setBearbeitenId(a.id);
    setOffen(true);
  };

  const loeschen = async (id) => {
    if (window.confirm("Angebot löschen?")) await deleteDoc(doc(db, "angebote", id));
  };

  const feld = (key, label, type = "text", placeholder = "") => (
    <div style={styles.feldZeile}>
      <label style={styles.label}>{label}</label>
      <input
        style={styles.input}
        type={type}
        value={formular[key]}
        onChange={(e) => setFormular((f) => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <div style={styles.seite}>
      <div style={styles.kopfzeile}>
        <h2 style={styles.titel}>🏷️ Angebote</h2>
        <button style={styles.neuBtn} onClick={() => { setFormular(LEER); setBearbeitenId(null); setOffen(true); }}>+ Neu</button>
      </div>

      {/* Formular */}
      {offen && (
        <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && setOffen(false)}>
          <div style={styles.modal}>
            <div style={styles.modalKopf}>
              <strong>{bearbeitenId ? "Angebot bearbeiten" : "Neues Angebot"}</strong>
              <button style={styles.schliessenBtn} onClick={() => setOffen(false)}>✕</button>
            </div>
            <form onSubmit={speichern}>
              {feld("titel", "Titel *", "text", "z.B. Sommerschuhe 50% rabatt")}
              {feld("geschaeft", "Geschäft", "text", "z.B. Intersport")}
              {feld("preis", "Preis", "text", "z.B. 39,99 €")}
              <div style={styles.datumZeile}>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>Von</label>
                  <input style={styles.input} type="date" value={formular.vonDatum} onChange={(e) => setFormular((f) => ({ ...f, vonDatum: e.target.value }))} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>Bis</label>
                  <input style={styles.input} type="date" value={formular.bisDatum} onChange={(e) => setFormular((f) => ({ ...f, bisDatum: e.target.value }))} />
                </div>
              </div>
              <div style={styles.feldZeile}>
                <div style={styles.labelZeile}>
                  <label style={styles.label}>Beschreibung</label>
                  <MikrofonButton onText={(t) => setFormular((f) => ({ ...f, beschreibung: f.beschreibung + (f.beschreibung ? " " : "") + t }))} />
                </div>
                <textarea style={{ ...styles.input, resize: "vertical", minHeight: "70px" }} value={formular.beschreibung} onChange={(e) => setFormular((f) => ({ ...f, beschreibung: e.target.value }))} placeholder="Details zum Angebot..." />
              </div>

              {/* Bild */}
              <div style={styles.feldZeile}>
                <label style={styles.label}>Bild</label>
                <div style={styles.bildButtons}>
                  <button type="button" style={styles.bildBtn} onClick={() => bildRef.current.click()} disabled={bildLaed}>📁 Datei</button>
                  <button type="button" style={styles.bildBtn} onClick={() => kameraRef.current.click()} disabled={bildLaed}>📷 Kamera</button>
                  <span style={styles.pasteHinweis}>oder Strg+V für Screenshot</span>
                  <input ref={bildRef} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => bildHochladen(e.target.files[0])} />
                  <input ref={kameraRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={(e) => bildHochladen(e.target.files[0])} />
                </div>
                {bildLaed && <p style={styles.hinweis}>⏳ Bild wird verarbeitet...</p>}
                {formular.bild && (
                  <div style={{ marginTop: "0.5rem", position: "relative", display: "inline-block" }}>
                    <img src={formular.bild} alt="Vorschau" style={styles.vorschau} />
                    <button type="button" style={styles.bildLoeschen} onClick={() => setFormular((f) => ({ ...f, bild: null }))}>✕</button>
                  </div>
                )}
              </div>

              <button type="submit" style={styles.speichernBtn}>{bearbeitenId ? "💾 Aktualisieren" : "💾 Speichern"}</button>
            </form>
          </div>
        </div>
      )}

      {/* Liste */}
      {angebote.length === 0 && <p style={styles.leer}>Noch keine Angebote.</p>}
      <div style={styles.grid}>
        {angebote.map((a) => (
          <div key={a.id} style={styles.karte}>
            {a.bild && <img src={a.bild} alt={a.titel} style={styles.karteBild} onClick={() => window.open(a.bild, "_blank")} />}
            <div style={styles.karteInhalt}>
              <div style={styles.karteKopf}>
                <strong style={styles.karteTitel}>{a.titel}</strong>
                {a.preis && <span style={styles.preisTag}>{a.preis}</span>}
              </div>
              {a.geschaeft && <div style={styles.geschaeft}>🏪 {a.geschaeft}</div>}
              {(a.vonDatum || a.bisDatum) && (
                <div style={styles.zeitraum}>📅 {a.vonDatum || "?"} – {a.bisDatum || "?"}</div>
              )}
              {a.beschreibung && <p style={styles.beschreibung}>{a.beschreibung}</p>}
              <div style={styles.karteAktionen}>
                <button style={styles.editBtn} onClick={() => bearbeiten(a)}>✏️ Bearbeiten</button>
                <button style={styles.loeschBtn} onClick={() => loeschen(a.id)}>🗑️</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  seite: { padding: "clamp(0.75rem, 2.5vw, 2rem)", width: "100%", boxSizing: "border-box" },
  kopfzeile: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" },
  titel: { margin: 0, color: "#1a3558" },
  neuBtn: { padding: "0.5rem 1.2rem", backgroundColor: "#1a73e8", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600" },
  overlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: "2rem", overflowY: "auto" },
  modal: { backgroundColor: "white", borderRadius: "10px", padding: "1.5rem", width: "500px", maxWidth: "95vw", boxShadow: "0 8px 30px rgba(0,0,0,0.2)", marginBottom: "2rem" },
  modalKopf: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.2rem" },
  schliessenBtn: { background: "none", border: "none", fontSize: "1.2rem", cursor: "pointer" },
  feldZeile: { marginBottom: "0.8rem" },
  label: { display: "block", fontSize: "0.82rem", color: "#555", marginBottom: "0.25rem", fontWeight: "500" },
  labelZeile: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  input: { width: "100%", padding: "0.5rem 0.7rem", border: "1px solid #ddd", borderRadius: "6px", fontSize: "0.95rem", boxSizing: "border-box", fontFamily: "sans-serif" },
  datumZeile: { display: "flex", gap: "1rem", marginBottom: "0.8rem" },
  bildButtons: { display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" },
  bildBtn: { padding: "0.4rem 0.8rem", backgroundColor: "#f0f2f5", border: "1px solid #ddd", borderRadius: "6px", cursor: "pointer", fontSize: "0.85rem" },
  pasteHinweis: { fontSize: "0.78rem", color: "#888" },
  vorschau: { maxWidth: "100%", maxHeight: "200px", borderRadius: "6px", objectFit: "contain", border: "1px solid #eee" },
  bildLoeschen: { position: "absolute", top: "4px", right: "4px", background: "rgba(0,0,0,0.6)", color: "white", border: "none", borderRadius: "50%", cursor: "pointer", width: "22px", height: "22px", fontSize: "0.7rem" },
  hinweis: { fontSize: "0.82rem", color: "#888", margin: "0.3rem 0 0" },
  speichernBtn: { width: "100%", padding: "0.7rem", backgroundColor: "#1a73e8", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "1rem", fontWeight: "600", marginTop: "0.5rem" },
  leer: { color: "#999", textAlign: "center", marginTop: "3rem" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1rem" },
  karte: { backgroundColor: "white", borderRadius: "10px", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
  karteBild: { width: "100%", height: "160px", objectFit: "cover", cursor: "pointer" },
  karteInhalt: { padding: "0.9rem" },
  karteKopf: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem", marginBottom: "0.3rem" },
  karteTitel: { fontSize: "1rem", color: "#1a3558", flex: 1 },
  preisTag: { backgroundColor: "#34a853", color: "white", padding: "0.2rem 0.5rem", borderRadius: "12px", fontSize: "0.85rem", fontWeight: "600", whiteSpace: "nowrap" },
  geschaeft: { fontSize: "0.85rem", color: "#555", marginBottom: "0.2rem" },
  zeitraum: { fontSize: "0.82rem", color: "#888", marginBottom: "0.4rem" },
  beschreibung: { fontSize: "0.88rem", color: "#444", margin: "0.4rem 0 0.6rem", lineHeight: 1.4 },
  karteAktionen: { display: "flex", gap: "0.5rem", justifyContent: "flex-end" },
  editBtn: { padding: "0.3rem 0.7rem", backgroundColor: "#f0f2f5", border: "1px solid #ddd", borderRadius: "4px", cursor: "pointer", fontSize: "0.82rem" },
  loeschBtn: { padding: "0.3rem 0.6rem", backgroundColor: "#fce8e6", border: "1px solid #f5c6c2", borderRadius: "4px", cursor: "pointer", fontSize: "0.82rem", color: "#c62828" },
};
